import { Controller } from "@hotwired/stimulus"
import { cable } from "@hotwired/turbo-rails"

export default class extends Controller {
  static values = {
    messagesLinkTarget: String
  }

  static targets = ["soundToggle", "soundOnIcon", "soundOffIcon", "soundLabel"]

  connect() {
    this.baseTitle = document.title
    this.titleCount = 0
    this.lastSoundAt = 0
    this.userInteracted = false
    this.soundEnabled = localStorage.getItem("notifications:sound") !== "false"
    this.audioContext = null
    this.refetchInProgress = false

    this.boundBeforeStreamRender = this.handleBeforeStreamRender.bind(this)
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    this.boundUserInteraction = this.markUserInteracted.bind(this)

    document.addEventListener("turbo:before-stream-render", this.boundBeforeStreamRender)
    document.addEventListener("visibilitychange", this.boundVisibilityChange)
    window.addEventListener("focus", this.boundVisibilityChange)

    document.addEventListener("pointerdown", this.boundUserInteraction, { capture: true, once: true })
    document.addEventListener("keydown", this.boundUserInteraction, { capture: true, once: true })
    document.addEventListener("touchstart", this.boundUserInteraction, { capture: true, once: true })
    document.addEventListener("click", this.boundUserInteraction, { once: true })

    this.observeCableSources()
    this.updateSoundIcon()
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.boundBeforeStreamRender)
    document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    window.removeEventListener("focus", this.boundVisibilityChange)
    this.stopObservingCableSources()
  }

  observeCableSources() {
    if (typeof MutationObserver === "undefined") return

    this.cableObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== "attributes" || mutation.attributeName !== "connected") continue
        const source = mutation.target
        if (source.tagName !== "TURBO-CABLE-STREAM-SOURCE") continue

        if (source.hasAttribute("connected")) {
          this.handleCableConnected(source)
        } else {
          this.handleCableDisconnected(source)
        }
      }
    })

    document.querySelectorAll("turbo-cable-stream-source").forEach((source) => {
      this.cableObserver.observe(source, { attributes: true, attributeFilter: ["connected"] })
    })
  }

  stopObservingCableSources() {
    if (this.cableObserver) {
      this.cableObserver.disconnect()
      this.cableObserver = null
    }
  }

  handleCableConnected(source) {
    this.debug("[notifications] cable connected", source.getAttribute("channel"))
  }

  handleCableDisconnected(source) {
    this.debug("[notifications] cable disconnected", source.getAttribute("channel"))
  }

  handleBeforeStreamRender(event) {
    const streamElement = event.detail.newStream
    if (!streamElement) return
    const target = streamElement.getAttribute("target")
    const action = streamElement.getAttribute("action")

    this.debug("[notifications] stream render", { target, action, expectedTarget: this.messagesLinkTargetValue })

    if (target !== this.messagesLinkTargetValue) return
    if (action !== "replace") return

    const previousCount = this.readMessagesCount()
    const originalRender = event.detail.render

    event.detail.render = async (streamElement) => {
      await originalRender(streamElement)
      const newCount = this.readMessagesCount()
      this.debug("[notifications] badge count changed", { previousCount, newCount })
      if (newCount > previousCount) {
        await this.handleNewMessages(newCount - previousCount)
      }
    }
  }

  async handleNewMessages(delta) {
    this.debug("[notifications] handleNewMessages", {
      delta,
      hidden: document.hidden,
      pathname: window.location.pathname,
      soundEnabled: this.soundEnabled,
      userInteracted: this.userInteracted,
      msSinceLastSound: Date.now() - this.lastSoundAt,
      audioContextState: this.audioContext?.state
    })

    if (document.hidden) {
      this.titleCount += delta
      this.updateTitle()
    }

    if (this.shouldPlaySound()) {
      this.debug("[notifications] will play sound")
      await this.playSound()
    } else {
      this.debug("[notifications] skip sound", {
        soundEnabled: this.soundEnabled,
        userInteracted: this.userInteracted,
        throttle: Date.now() - this.lastSoundAt < 3000,
        hidden: document.hidden,
        conversationPage: this.isConversationPage()
      })
    }
  }

  async handleVisibilityChange() {
    const isVisible = !document.hidden
    this.debug("[notifications] visibility change", { isVisible })

    if (isVisible) {
      if (this.titleCount > 0) {
        this.titleCount = 0
        document.title = this.baseTitle
      }

      await this.ensureCableConnected()
      await this.refetchUnreadCount()
    }
  }

  async ensureCableConnected() {
    const disconnectedSources = Array.from(document.querySelectorAll("turbo-cable-stream-source"))
      .filter((source) => !source.hasAttribute("connected"))

    if (disconnectedSources.length === 0) return

    this.debug("[notifications] forcing cable reconnect")

    try {
      if (cable && cable.getConsumer) {
        const consumer = await cable.getConsumer()
        if (consumer && typeof consumer.connect === "function") {
          consumer.connect()
        }
      }
    } catch (error) {
      this.debug("[notifications] не удалось форсировать reconnect:", error)
    }
  }

  async refetchUnreadCount() {
    if (this.refetchInProgress) return
    this.refetchInProgress = true

    try {
      const response = await fetch("/messages/unread_count", {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return

      const data = await response.json()
      const newCount = parseInt(data.count, 10) || 0
      const previousCount = this.readMessagesCount()

      if (newCount !== previousCount) {
        this.updateMessagesBadge(newCount)

        if (document.hidden && newCount > 0) {
          this.titleCount = newCount
          this.updateTitle()
        }
      }
    } catch (error) {
      this.debug("[notifications] не удалось обновить счётчик:", error)
    } finally {
      this.refetchInProgress = false
    }
  }

  updateMessagesBadge(count) {
    const container = document.getElementById(this.messagesLinkTargetValue)
    if (!container) return

    const link = container.querySelector("a")
    if (!link) return

    let badge = link.querySelector("[data-messages-badge]")

    if (count <= 0) {
      if (badge) badge.remove()
      return
    }

    const text = count > 99 ? "99+" : count.toString()

    if (!badge) {
      badge = document.createElement("span")
      badge.dataset.messagesBadge = ""
      badge.className = "absolute -top-2 -right-3 lg:top-1 lg:right-1 min-w-[1.125rem] h-[1.125rem] flex items-center justify-center bg-blue-600 text-white text-[10px] font-bold rounded-full px-1"
      link.appendChild(badge)
    }

    badge.textContent = text
  }

  shouldPlaySound() {
    if (!this.soundEnabled) return false
    if (!this.userInteracted) return false
    if (Date.now() - this.lastSoundAt < 3000) return false
    if (document.hidden) return true
    return !this.isConversationPage()
  }

  isConversationPage() {
    return /^\/messages\/\d+/.test(window.location.pathname)
  }

  readMessagesCount() {
    const container = document.getElementById(this.messagesLinkTargetValue)
    if (!container) return 0
    const badge = container.querySelector("[data-messages-badge]")
    if (!badge) return 0
    const count = parseInt(badge.textContent.trim(), 10)
    return isNaN(count) ? 0 : count
  }

  updateTitle() {
    if (this.titleCount > 0) {
      document.title = `(${this.titleCount}) Новые сообщения — ${this.baseTitle}`
    }
  }

  markUserInteracted() {
    this.userInteracted = true
    this.debug("[notifications] user gesture detected")

    if (this.audioContext && this.audioContext.state === "suspended") {
      this.audioContext.resume().catch((error) => {
        this.debug("[notifications] resume after gesture failed", error)
      })
    }
  }

  async playSound() {
    this.debug("[notifications] playSound entered")
    const now = Date.now()
    if (now - this.lastSoundAt < 3000) {
      this.debug("[notifications] playSound skip throttle")
      return
    }

    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        this.debug("[notifications] audio context created", { state: this.audioContext.state })
      }

      const ctx = this.audioContext
      this.debug("[notifications] ctx.state before resume", ctx.state)

      if (ctx.state === "suspended") {
        this.debug("[notifications] resuming suspended context")
        const resumePromise = ctx.resume()
        resumePromise.catch((error) => {
          this.debug("[notifications] resume rejected", error)
        })
        await Promise.race([
          resumePromise,
          new Promise((resolve) => setTimeout(() => resolve("timeout"), 50))
        ])
        this.debug("[notifications] resume race finished", { state: ctx.state })
      }

      this.debug("[notifications] creating oscillator")
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()

      oscillator.type = "sine"
      oscillator.frequency.setValueAtTime(880, ctx.currentTime)
      oscillator.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15)

      gain.gain.setValueAtTime(0.08, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15)

      oscillator.connect(gain)
      gain.connect(ctx.destination)

      oscillator.start()
      this.debug("[notifications] oscillator.start executed")
      oscillator.stop(ctx.currentTime + 0.15)
      this.debug("[notifications] oscillator.stop scheduled")

      this.lastSoundAt = now
    } catch (error) {
      this.debug("[notifications] Не удалось воспроизвести звук:", error)
    }
  }

  debug(...args) {
    if (this.isDevelopment()) {
      console.debug(...args)
    }
  }

  isDevelopment() {
    return document.documentElement.dataset.env === "development" ||
           (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  }

  toggleSound() {
    this.soundEnabled = !this.soundEnabled
    localStorage.setItem("notifications:sound", this.soundEnabled.toString())
    this.updateSoundIcon()
  }

  updateSoundIcon() {
    if (this.hasSoundOnIconTarget && this.hasSoundOffIconTarget && this.hasSoundToggleTarget) {
      this.soundOnIconTarget.classList.toggle("hidden", !this.soundEnabled)
      this.soundOffIconTarget.classList.toggle("hidden", this.soundEnabled)
      this.soundToggleTarget.setAttribute(
        "aria-label",
        this.soundEnabled ? "Выключить звук уведомлений" : "Включить звук уведомлений"
      )
    }

    if (this.hasSoundLabelTarget) {
      this.soundLabelTarget.textContent = this.soundEnabled ? "Звук: вкл" : "Звук: выкл"
    }
  }
}
